import { useExperiments, useTranslation } from '@wix/fe-essentials/react';
import { FixerType, RuleListStatus } from '@wix/rules-panel-ui';
import { useAtom, useSetAtom } from 'jotai';
import React, { useCallback } from 'react';
import { currentTaskInfoAtom } from '../../../atoms/bi-atoms';
import { ruleApplyTriggeredAtom } from '../../../atoms/session-atoms';
import { BiCtaNames } from '../../../bi/types';
import { convertScanStatusToUiStatus } from '../../../components/fixers/alt-image-fixer/utils';
import { RuleDescriptionComponent } from '../../../components/rule-description-component';
import { SITE_LEVEL_PAGE_ID } from '../../../consts';
import { usePanelStateAtom } from '../../../hooks/rules-state';
import { useA11yWizardAdapter } from '../../../hooks/use-a11y-wizard-adapter';
import { useBiLogger } from '../../../hooks/use-bi-logger';
import { useOpenArticleByEditorHost } from '../../../hooks/use-open-article-by-editor-host';
import { getFixerDesignPropsFromCapabilities, getFooterButtonTypesFromCapabilities } from '../../../utils/get-fixer-design-props';
import { useRuleBase } from '../../shared/use-rule-base';
import { getUpdateDomOrderRuleStateOnApply } from './get-update-dom-order-rule-state-on-apply';
import { getRadioOptions } from './radio-options';
import { useDomOrderOnApply } from './use-dom-order-on-apply';
export const DOM_ORDER_FIX_OPTION = {
  AUTO: 'auto',
  MANUAL: 'manual'
};
export const useDomOrderRule = () => {
  const {
    experiments
  } = useExperiments();
  const {
    getRuleState
  } = usePanelStateAtom();
  const {
    updateRuleStateAndVerifyIfNeeded,
    setIsApplyTriggered
  } = useRuleBase();
  const {
    t
  } = useTranslation();
  const {
    a11yWizardAdapter,
    capabilities,
    editorHost
  } = useA11yWizardAdapter();
  const {
    openSelectedArticle
  } = useOpenArticleByEditorHost();
  const setRuleApplyTriggered = useSetAtom(ruleApplyTriggeredAtom);
  const {
    getOnApply
  } = useDomOrderOnApply({
    setIsApplyTriggered,
    setRuleApplyTriggered
  });
  const biLogger = useBiLogger();
  const [currentTaskInfo, setCurrentTaskInfo] = useAtom(currentTaskInfoAtom);
  const isLanguageRTL = a11yWizardAdapter.getIsSiteLanguageRTL();
  const getDomOrderProps = useCallback(_ref => {
    var _getRuleState, _ruleState$state, _ruleState$state2;
    let {
      ruleExecutionResult
    } = _ref;
    const {
      result,
      context,
      ruleId
    } = ruleExecutionResult;
    const {
      status
    } = result;
    const {
      isManuallyCompleted,
      siteCreationDate,
      siteLanguage,
      isAutomaticallyCompleted
    } = context;
    const updatedSiteLang = ((_getRuleState = getRuleState(SITE_LEVEL_PAGE_ID, ruleId)) == null || (_getRuleState = _getRuleState.state) == null ? void 0 : _getRuleState.siteLanguage) ?? siteLanguage;
    const ruleState = getRuleState(SITE_LEVEL_PAGE_ID, ruleId);
    const isManuallyCompletedUIValue = (ruleState == null || (_ruleState$state = ruleState.state) == null ? void 0 : _ruleState$state.isManuallyCompleted) ?? isManuallyCompleted;
    const uiStatus = (ruleState == null || (_ruleState$state2 = ruleState.state) == null ? void 0 : _ruleState$state2.status) ?? convertScanStatusToUiStatus(status);
    if (status === 'not-applicable') {
      return null;
    }
    const updateRuleStateOnApply = getUpdateDomOrderRuleStateOnApply(siteCreationDate, updatedSiteLang, updateRuleStateAndVerifyIfNeeded, a11yWizardAdapter, experiments);
    const onApply = getOnApply({
      isManuallyCompleted: isManuallyCompletedUIValue,
      uiStatus,
      updateRuleState: updateRuleStateOnApply
    });
    const radioOptions = getRadioOptions(t, capabilities.compactPanelChrome);
    const radioGroupLabel = t('accessibility-wizard.rules-list.dom-order.organize-dom-order');
    const completedRuleContentValueManually = t('accessibility-wizard.rules-list.dom-order.input-value');
    const completedRuleContentValueAuto = t('accessibility-wizard.rules-list.dom-order.input-value.automatically');
    const isEditable = capabilities.usesClassicModalChrome || isManuallyCompletedUIValue;
    const editorCompletedValue = isManuallyCompletedUIValue ? completedRuleContentValueManually : completedRuleContentValueAuto;
    const completedRuleValue = capabilities.usesClassicModalChrome ? editorCompletedValue : completedRuleContentValueManually;
    const completedRuleContent = {
      editButtonLabel: t('accessibility-wizard.common.edit'),
      inputTitle: radioGroupLabel,
      title: t('accessibility-wizard.rules-list.issue-resolved'),
      value: completedRuleValue
    };
    const defaultValueForClassic = isManuallyCompletedUIValue ? radioOptions[1] : isAutomaticallyCompleted ? radioOptions[0] : undefined;
    const defaultValue = capabilities.usesClassicModalChrome ? defaultValueForClassic : isManuallyCompletedUIValue ? radioOptions[1] : undefined;
    return {
      description: /*#__PURE__*/React.createElement(RuleDescriptionComponent, {
        i18nKey: isManuallyCompletedUIValue ? 'accessibility-wizard.rules-list.dom-order.description.manually-completed' : uiStatus !== RuleListStatus.failed ? 'accessibility-wizard.rules-list.page-auto-dom-order.description.passed.automatically' : isLanguageRTL ? 'accessibility-wizard.rules-list.dom-order.RTL-description.failed' : 'accessibility-wizard.rules-list.dom-order.description.failed',
        openArticle: () => {
          openSelectedArticle(editorHost, 'editor-package-accessibility-wizard.articles-organizing-DOM-help');
          biLogger.taskCtaClick({
            ctaName: BiCtaNames.LearnMore,
            elementCount: 1,
            failedElementCount: Number(status === 'failed')
          });
        },
        textButtonAriaLabel: t('accessibility-wizard.rules-list.dom-order.description.learn-more.aria-label')
      }),
      fixer: FixerType.RadioGroupRule,
      fixerProps: {
        completedRuleContent,
        defaultValue: uiStatus === RuleListStatus.passed ? defaultValue : undefined,
        design: getFixerDesignPropsFromCapabilities(capabilities),
        footerLabels: {
          buttonTypes: getFooterButtonTypesFromCapabilities(capabilities),
          dismiss: t('accessibility-wizard.rules-list.dom-order.footer-button.cancel'),
          submit: t('accessibility-wizard.rules-list.dom-order.footer-button.apply')
        },
        onApply,
        onSelect: selectedValue => {
          biLogger.elementFieldChange({
            index: 0,
            status: uiStatus,
            value: `${selectedValue}`
          });
        },
        radioGroupLabel,
        radioOptions
      },
      id: ruleId,
      isEditableOnceCompleted: isEditable,
      label: t('accessibility-wizard.rules-list.dom-order.title'),
      onRuleToggle: function (toggleType, ruleIndex, initiatedByUser) {
        if (initiatedByUser === void 0) {
          initiatedByUser = false;
        }
        setCurrentTaskInfo(taskInfo => ({
          ...taskInfo,
          status: uiStatus,
          task_index: ruleIndex,
          task_name: ruleId
        }));
        biLogger.taskExpandCollapse({
          elementCount: 1,
          failedElementCount: Number(status === 'failed'),
          initiatedByUser,
          taskInfo: {
            ...currentTaskInfo,
            status: uiStatus,
            task_index: ruleIndex,
            task_name: ruleId
          },
          toggleType
        });
      },
      onStatusChange: () => {
        setCurrentTaskInfo(taskInfo => ({
          ...taskInfo,
          status: uiStatus
        }));
        biLogger.taskStatusChange({
          elementCount: 1,
          failedElementCount: Number(status === 'failed'),
          status: uiStatus
        });
        biLogger.elementStatusChange({
          index: 0,
          status: uiStatus
        });
      },
      parentGroupId: SITE_LEVEL_PAGE_ID,
      ruleId,
      status: uiStatus
    };
  }, [a11yWizardAdapter, biLogger, capabilities, currentTaskInfo, editorHost, experiments, getOnApply, getRuleState, isLanguageRTL, openSelectedArticle, setCurrentTaskInfo, t, updateRuleStateAndVerifyIfNeeded]);
  return {
    getDomOrderProps
  };
};
//# sourceMappingURL=use-dom-order-rule.js.map